import { motion } from "framer-motion";
import { springs } from "../motion";
import { ACTIVITY_CATEGORY_CONFIG } from "./types";
import type { ActivityEventCategory } from "./types";

// ─── Timeline Dot ───────────────────────────────────────────────────────────

interface ActivityTimelineDotProps {
  category: ActivityEventCategory;
  index: number;
  actor: { initials: string; avatarColor: string };
}

export function ActivityTimelineDot({
  category,
  index,
  actor,
}: ActivityTimelineDotProps) {
  const config = ACTIVITY_CATEGORY_CONFIG[category];
  const Icon = config.icon;
  const isSystem = category === "system";

  if (isSystem) {
    return (
      <motion.div
        className="relative z-10 flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: config.bgColor }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ ...springs.bouncy, delay: index * 0.04 }}
      >
        <Icon size={13} style={{ color: config.color }} />
      </motion.div>
    );
  }

  return (
    <motion.div
      className="relative z-10 flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full"
      style={{ backgroundColor: actor.avatarColor }}
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ ...springs.bouncy, delay: index * 0.04 }}
    >
      <span className="text-[11px] font-semibold text-white select-none">
        {actor.initials}
      </span>

      {/* Category overlay dot */}
      <motion.div
        className="absolute -bottom-0.5 -right-0.5 flex h-[13px] w-[13px] items-center justify-center rounded-full border-[1.5px] border-white"
        style={{ backgroundColor: config.bgColor }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ ...springs.bouncy, delay: index * 0.04 + 0.1 }}
      >
        <Icon size={7} style={{ color: config.color }} />
      </motion.div>
    </motion.div>
  );
}

// ─── Timeline Connector ─────────────────────────────────────────────────────

interface ActivityTimelineConnectorProps {
  index: number;
}

export function ActivityTimelineConnector({
  index,
}: ActivityTimelineConnectorProps) {
  return (
    <motion.div
      className="w-px flex-1 self-stretch mx-auto"
      style={{
        transformOrigin: "top",
        background:
          "linear-gradient(to bottom, var(--color-border) 10%, var(--color-border) 90%, transparent)",
      }}
      initial={{ scaleY: 0 }}
      animate={{ scaleY: 1 }}
      transition={{ ...springs.gentle, delay: index * 0.04 + 0.05 }}
    />
  );
}
